// Messages a bidder sees after pressing Bid: short enough to read at a
// glance in a toast, and a failed bid always says it was not placed. The
// server's own texts are technical, and an HTTP error used to show only
// "Something went wrong!".

export const NOT_REACHED_MESSAGE = "Bid not placed. Check your internet.";
export const SESSION_ENDED_MESSAGE = "Session ended. Please sign in again.";
export const NOT_APPROVED_MESSAGE = "Not approved for this lot.";
export const CONCLUDED_MESSAGE = "Auction ended.";
export const CHECK_AMOUNT_MESSAGE = "Invalid bid amount.";
export const TOO_MANY_MESSAGE = "Too many tries. Please wait.";
export const SERVER_PROBLEM_MESSAGE =
  "Bid not placed. Please refresh and try again.";

const serverText = (data) => {
  if (typeof data === "string") return data;
  return data?.error || data?.message || "";
};

// An error thrown by axios for a bid (or proxy bid) request.
// sessionEnded: the bidder has to sign in again.
export const getBidErrorMessage = (error) => {
  const response = error?.response;
  if (!response) {
    return { message: NOT_REACHED_MESSAGE, sessionEnded: false };
  }

  const text = serverText(response.data);
  switch (response.status) {
    case 401:
      return { message: SESSION_ENDED_MESSAGE, sessionEnded: true };
    case 403:
      return { message: NOT_APPROVED_MESSAGE, sessionEnded: false };
    case 429:
      return { message: TOO_MANY_MESSAGE, sessionEnded: false };
    case 400:
      if (/concluded/i.test(text)) {
        return { message: CONCLUDED_MESSAGE, sessionEnded: false };
      }
      // proxy bid and post-bid rules already read well
      if (/^(proxy|bid amount|bid is not within)/i.test(text)) {
        return { message: text, sessionEnded: false };
      }
      return { message: CHECK_AMOUNT_MESSAGE, sessionEnded: false };
    default:
      return { message: SERVER_PROBLEM_MESSAGE, sessionEnded: false };
  }
};

// Never mention other bidders (no "price changed", no "same price"), and
// never say "recorded": whether a rejected bid is saved is for the logs.
const LOT_ENDED = "Bid not accepted. Bidding has ended.";
const NOT_STARTED = "Bid not accepted. Bidding not started yet.";
const SAME_AMOUNT = "You already accepted this round's price.";
const MISSED_ROUNDS = "Bid not accepted. Too many missed rounds.";
const CHECK_AMOUNT = "Bid not accepted. Check the amount.";

// The server did not accept the bid (status "FAILURE").
export const getBidRejectedMessage = (reason) => {
  const text = (reason || "").trim();

  if (/has ended/i.test(text)) return LOT_ENDED;
  if (/not started yet/i.test(text)) return NOT_STARTED;
  if (/same amount/i.test(text)) return SAME_AMOUNT;
  if (/missed bids/i.test(text)) return MISSED_ROUNDS;
  // the amount rule (or no reason at all); the lot is reloaded afterwards
  if (!text || /invalid bid amount|less than the previous bid/i.test(text)) {
    return CHECK_AMOUNT;
  }
  // anything else: the server's own words
  return text;
};

// The bid was accepted. The server says "under pre bid period" when it was
// taken as a pre-bid.
export const getBidAcceptedMessage = (reason, amountText) => {
  const amount = amountText ? ` of ${amountText}` : "";
  if (/pre bid period/i.test(reason || "")) {
    return `Pre-bid${amount} recorded.`;
  }
  return `Bid${amount} placed.`;
};

// Post-bid (the window after a lot ends). The server answers 400 with its own
// sentence; the start price it mentions is not repeated to the bidder.
export const POST_BID_CLOSED_MESSAGE = "Post-bid is not open for this lot.";

export const getPostBidErrorMessage = (error) => {
  const response = error?.response;
  if (response?.status !== 400) return getBidErrorMessage(error);

  const text = serverText(response.data);
  if (/time frame/i.test(text)) {
    return { message: POST_BID_CLOSED_MESSAGE, sessionEnded: false };
  }
  return { message: CHECK_AMOUNT_MESSAGE, sessionEnded: false };
};

export const getPostBidAcceptedMessage = (amountText) =>
  amountText ? `Post-bid of ${amountText} placed.` : "Post-bid placed.";
